"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_modal_1 = __importDefault(require("react-modal"));
const common_styled_components_1 = require("../../../common-styled-components");
const styled_components_1 = __importDefault(require("styled-components"));
const theme_1 = require("../../../../theme");
const useSendFile_1 = __importDefault(require("../../../../hooks/useSendFile"));
const FileUploadInput_1 = require("./FileUploadInput");
const PDFPreview_1 = require("../../../generic/PDFPreview");
const PeriodicalStatementForm_1 = require("./PeriodicalStatementForm");
const BunkerDeliveryNoteForm_1 = require("./BunkerDeliveryNoteForm");
const timeUtils_1 = require("../../../../util/timeUtils");
const useMetadata_1 = require("../../../../hooks/useMetadata");
const transactionAttachmentUtil_1 = require("../../admin/util/transactionAttachmentUtil");
const getModalStyles = (wide) => ({
    ...common_styled_components_1.baseReactModalStyles,
    content: {
        ...common_styled_components_1.baseReactModalStyles.content,
        minWidth: "480px",
        ...(wide ? { width: "1140px", maxWidth: "95vw" } : {}),
        display: "flex",
    },
});
const ModalContent = styled_components_1.default.div `
  display: flex;
  flex-direction: column;
  gap: ${theme_1.theme.spacing(3)};
  width: 100%;
`;
const Form = styled_components_1.default.form `
  display: flex;
  flex-direction: column;
  gap: ${theme_1.theme.spacing(3)};
`;
const FileNameContainer = styled_components_1.default.div `
  display: flex;
  flex-direction: column;
  gap: ${theme_1.theme.spacing(1)};
  padding: ${theme_1.theme.spacing(2)};
  background-color: ${theme_1.theme.colors.grey(5)};
  border-radius: 4px;
`;
const FileNameLabel = styled_components_1.default.span `
  font-size: 0.875rem;
  color: ${theme_1.theme.colors.grey(2)};
  font-weight: 500;
`;
const FileName = styled_components_1.default.span `
  font-family: monospace;
  word-break: break-all;
`;
const ToolsRow = styled_components_1.default.div `
  display: flex;
  gap: ${theme_1.theme.spacing(1)};
`;
const ContentRow = styled_components_1.default.div `
  display: flex;
  flex-direction: column;
  gap: ${theme_1.theme.spacing(3)};

  @media (min-width: 920px) {
    flex-direction: row;
    align-items: flex-start;
    gap: ${theme_1.theme.spacing(4)};
  }
`;
const FormColumn = styled_components_1.default.div `
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;
const TransactionAttachmentUploader = ({ shipId, style, attachmentType, refresh, periodicalStatementOptions, selectedYear, initialFiles, isOpen, onClose, onOpen, }) => {
    const [metadata] = (0, useMetadata_1.useMetadata)();
    const [queuedFiles, setQueuedFiles] = (0, react_1.useState)([]);
    const [processedCount, setProcessedCount] = (0, react_1.useState)(0);
    const [submitting, setSubmitting] = (0, react_1.useState)(false);
    const [errorText, setErrorText] = (0, react_1.useState)("");
    const [isWideScreen, setIsWideScreen] = (0, react_1.useState)(() => window.matchMedia(`(min-width: ${theme_1.breakpoints.tablet}px)`).matches);
    const [sendFile, fileSendMessage, resetSendStatus] = (0, useSendFile_1.default)("admin/add_transaction_attachment");
    (0, react_1.useEffect)(() => {
        const mq = window.matchMedia(`(min-width: ${theme_1.breakpoints.tablet}px)`);
        const handler = (e) => setIsWideScreen(e.matches);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);
    const activeFile = queuedFiles[0] ?? null;
    const totalFilesCount = processedCount + queuedFiles.length;
    // Initialize file data based on type
    const getInitialAttachmentData = (0, react_1.useCallback)((year) => {
        const type = attachmentType;
        const dateNow = new Date();
        const yearNow = dateNow.getFullYear();
        const monthStartEnd = yearNow === year
            ? (0, timeUtils_1.getMonthStartEnd)(dateNow)
            : (0, timeUtils_1.getMonthStartEnd)(new Date(year, 11, 15));
        const baseData = {
            type,
            ship_id: shipId,
            year,
        };
        switch (type) {
            case 1:
                return {
                    ...baseData,
                    period_start: monthStartEnd
                        ? (0, timeUtils_1.formatToDateInputValue)(monthStartEnd.start)
                        : undefined,
                    period_end: monthStartEnd
                        ? (0, timeUtils_1.formatToDateInputValue)(monthStartEnd.end)
                        : undefined,
                };
            case 2:
                return {
                    ...baseData,
                    date: (0, timeUtils_1.formatToDateInputValue)(dateNow),
                };
            default:
                return { ...baseData };
        }
    }, [attachmentType, shipId]);
    const [attachmentData, setAttachmentData] = (0, react_1.useState)(getInitialAttachmentData(selectedYear));
    // When the modal opens (or initialFiles changes while open), reset the queue
    (0, react_1.useEffect)(() => {
        if (isOpen) {
            setQueuedFiles(initialFiles);
            setProcessedCount(0);
            setErrorText("");
        }
    }, [initialFiles, isOpen]);
    // Reset form data when the active file changes
    (0, react_1.useEffect)(() => {
        setAttachmentData(getInitialAttachmentData(selectedYear));
        resetSendStatus();
    }, [activeFile, getInitialAttachmentData, resetSendStatus, selectedYear]);
    const handleFileChange = (0, react_1.useCallback)((selectedFile) => {
        if (selectedFile === null) {
            setErrorText("File size exceeds 30MB limit.");
            setQueuedFiles([]);
        }
        else {
            setErrorText("");
            setQueuedFiles([selectedFile]);
            setProcessedCount(0);
        }
    }, []);
    const handleClose = (0, react_1.useCallback)(() => {
        setQueuedFiles([]);
        setProcessedCount(0);
        resetSendStatus();
        setErrorText("");
        onClose();
    }, [onClose, resetSendStatus]);
    const moveToNextQueuedFile = (0, react_1.useCallback)((shouldRefresh) => {
        const remainingFiles = queuedFiles.slice(1);
        if (remainingFiles.length === 0) {
            // Last file — refresh before closing so the parent doesn't unmount mid-queue
            if (shouldRefresh) {
                refresh?.();
            }
            handleClose();
            return;
        }
        setProcessedCount((prev) => prev + 1);
        setQueuedFiles(remainingFiles);
        // Do NOT refresh here: calling refresh on an intermediate file causes the
        // parent (ShipAdmin) to transition to a Loading state and unmount this
        // component, destroying the remaining queue.
    }, [handleClose, queuedFiles, refresh]);
    const handleSkipCurrentFile = (0, react_1.useCallback)(() => {
        if (!activeFile)
            return;
        moveToNextQueuedFile(false);
    }, [activeFile, moveToNextQueuedFile]);
    const updateValue = (0, react_1.useCallback)((key, value) => {
        setAttachmentData((prev) => ({ ...prev, [key]: value }));
    }, []);
    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!activeFile || errorText !== "")
            return;
        const formData = new FormData();
        formData.append("ship_id", shipId.toString());
        formData.append("type", attachmentType.toString());
        formData.append("attachment", activeFile);
        // Add all file data fields to form data
        Object.entries(attachmentData).forEach(([key, value]) => {
            if (key === "proof_of_sustainability_notes") {
                return;
            }
            else if (Array.isArray(value)) {
                formData.append(key, JSON.stringify(value));
            }
            else if (value !== null && value !== undefined && key !== "type") {
                formData.append(key, value.toString());
            }
        });
        setSubmitting(true);
        const uploadSucceeded = await sendFile(formData);
        setSubmitting(false);
        if (uploadSucceeded) {
            moveToNextQueuedFile(true);
        }
    };
    const disabled = activeFile === null || errorText !== "";
    const renderForm = () => {
        switch (attachmentType) {
            case 1:
                return ((0, jsx_runtime_1.jsx)(PeriodicalStatementForm_1.PeriodicalStatementForm, { state: attachmentData, updateValue: updateValue, metadata: metadata }));
            case 2:
                return ((0, jsx_runtime_1.jsx)(BunkerDeliveryNoteForm_1.BunkerDeliveryNoteForm, { state: attachmentData, updateValue: updateValue, periodicalStatementOptions: periodicalStatementOptions, metadata: metadata }));
            default:
                return null;
        }
    };
    const isPDF = activeFile?.type === "application/pdf";
    const showWideLayout = isPDF && isWideScreen;
    return ((0, jsx_runtime_1.jsxs)("div", { style: style, children: [(0, jsx_runtime_1.jsx)(react_modal_1.default, { isOpen: isOpen, onRequestClose: handleClose, contentLabel: `Add ${transactionAttachmentUtil_1.ATTACHMENT_TYPE_LABELS[attachmentType].singular}`, style: getModalStyles(showWideLayout), shouldCloseOnEsc: true, children: (0, jsx_runtime_1.jsxs)(ModalContent, { children: [(0, jsx_runtime_1.jsxs)("h2", { children: ["Add ", transactionAttachmentUtil_1.ATTACHMENT_TYPE_LABELS[attachmentType].singular] }), (0, jsx_runtime_1.jsxs)(ContentRow, { children: [(0, jsx_runtime_1.jsxs)(FormColumn, { children: [(0, jsx_runtime_1.jsxs)(Form, { onSubmit: handleSubmit, children: [!activeFile && ((0, jsx_runtime_1.jsx)(FileUploadInput_1.FileUploadInput, { onFileChange: handleFileChange, error: errorText })), activeFile && ((0, jsx_runtime_1.jsxs)(FileNameContainer, { children: [(0, jsx_runtime_1.jsxs)(FileNameLabel, { children: ["Current file (", processedCount + 1, " / ", totalFilesCount, ")"] }), (0, jsx_runtime_1.jsx)(FileName, { children: activeFile.name }), queuedFiles.length > 1 && ((0, jsx_runtime_1.jsxs)(common_styled_components_1.P, { children: [queuedFiles.length - 1, " more file(s) queued after this one."] }))] })), activeFile && renderForm(), (0, jsx_runtime_1.jsx)(common_styled_components_1.Button, { type: "submit", disabled: disabled || submitting, children: disabled
                                                        ? "Select file to upload"
                                                        : submitting
                                                            ? "Submitting file"
                                                            : queuedFiles.length > 1
                                                                ? "Save and continue"
                                                                : "Upload" })] }), fileSendMessage && (0, jsx_runtime_1.jsx)("p", { children: fileSendMessage }), (0, jsx_runtime_1.jsxs)(ToolsRow, { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.SecondaryButton, { type: "button", onClick: handleSkipCurrentFile, disabled: !activeFile || submitting, children: queuedFiles.length > 1 ? "Skip file" : "Remove file" }), (0, jsx_runtime_1.jsx)(common_styled_components_1.SecondaryButton, { type: "button", onClick: handleClose, children: "Close" })] })] }), isPDF && ((0, jsx_runtime_1.jsx)(PDFPreview_1.PDFPreview, { file: activeFile, width: 600, height: 600 }))] })] }) }), (0, jsx_runtime_1.jsxs)(common_styled_components_1.SecondaryButton, { onClick: onOpen, children: ["+ Add ", transactionAttachmentUtil_1.ATTACHMENT_TYPE_LABELS[attachmentType].singular] })] }));
};
exports.default = TransactionAttachmentUploader;
